// Shows active admin announcements on the student and teacher dashboards.
// Renders nothing when there are none, or when the request fails.
import React, { useEffect, useState } from "react";
import { Megaphone, X } from "lucide-react";
import api from "../../services/api";
import "./AnnouncementBanner.css";

const STORAGE_KEY = "eg_dismissed_announcements";

const readDismissed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const writeDismissed = (ids) => {
  try {
    // Keep the list from growing forever
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-100)));
  } catch {
    /* storage unavailable: dismissal lasts for this visit only */
  }
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const AnnouncementBanner = () => {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/announcements")
      .then((res) => {
        if (!cancelled) setItems(Array.isArray(res.data?.announcements) ? res.data.announcements : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = items.filter((a) => a && a.id !== undefined && !dismissed.includes(String(a.id)));
  if (visible.length === 0) return null;

  const dismiss = (id) => {
    const next = [...dismissed, String(id)];
    setDismissed(next);
    writeDismissed(next);
  };

  return (
    <div className="eg-announcements" role="region" aria-label="Announcements">
      {visible.map((a) => (
        <article key={a.id} className="eg-announcement">
          <div className="eg-announcement-icon" aria-hidden="true">
            <Megaphone size={18} />
          </div>
          <div className="eg-announcement-body">
            <h3>{a.title}</h3>
            {a.body && <p>{a.body}</p>}
            {a.created_at && <time dateTime={a.created_at}>{formatDate(a.created_at)}</time>}
          </div>
          <button
            type="button"
            className="eg-announcement-close"
            onClick={() => dismiss(a.id)}
            aria-label={`Dismiss announcement: ${a.title}`}
            title="Dismiss"
          >
            <X size={18} />
          </button>
        </article>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
